import {Card, Container, createStyles, Group, Menu, rem, Space, Tabs, Text, Title, UnstyledButton} from "@mantine/core";
import {IconChevronDown} from "@tabler/icons-react";
import {useLocation, useNavigate} from "react-router-dom";
import {PropsWithChildren, useCallback} from "react";
import {Settlement, SettlementEntity} from "@ots-frontend/ots-data";
import {SettlementDetails} from "./SettlementDetails";
import {useTranslation} from "react-i18next";
import {SettlementAction, SettlementActionMenuDropdown} from "../../menu/SettlementActionMenu/SettlementActionMenu";
import {ObjectChangeLogView} from "../common/ObjectChangeLogView/ObjectChangeLogView";
import {TabItem} from "../../tabs/Tabs/Tabs";
import {HasAccess} from "../../auth/HasAccess/HasAccess";
import {FormattedActivityPeriodDate} from "../../date/FormattedActivityPeriodDate/FormattedActivityPeriodDate";
import {FormattedDatabaseCode} from "../../text/FormattedDatabaseCode/FormattedDatabaseCode";
import {SettlementInsights} from "./SettlementInsights";
import {SettlementFormModal, SettlementFormValues} from "../../form/SettlementForm/SettlementForm";
import {useDisclosure} from "@mantine/hooks";
import {useSettlementMutation} from "../../../hooks/useSettlementMutation";
import {useApp} from "../../../providers/RegionProvider/RegionProvider";

const useStyles = createStyles((theme) => ({
  mainSection: {
    paddingBottom: theme.spacing.sm
  }
}));

export type SettlementProfileProps = PropsWithChildren<{
  settlement: SettlementEntity
}>

const tabs: TabItem[] = [
  {
    key: "details",
    value: "Details"
  },
  {
    key: "insights",
    value: "Insights"
  },
  {
    key: "history",
    value: "Change Logs",
    permissions: ["change_log_view"]
  },
];

const FallbackTitle = (props: SettlementProfileProps) => {
  const {settlement} = props;

  return (
    <>
      <FormattedActivityPeriodDate
        reportTimeType={settlement.activityPeriodType}
        fromDate={settlement.activityPeriodFromDttm}
        toDate={settlement.activityPeriodToDttm}/>
      {" - "}
      <FormattedDatabaseCode value={settlement.activityPeriodType}/>
    </>
  )
}

export const SettlementProfile = (props: SettlementProfileProps) => {
  const {settlement} = props;

  const {classes} = useStyles();

  const {regionId} = useApp()
  const {t} = useTranslation()
  const navigate = useNavigate();
  const {pathname, search, state} = useLocation();

  const [formOpen, formHandler] = useDisclosure(false)

  const {
    updateSettlement,
    isUpdating,
  } = useSettlementMutation()

  const onSettlementActionClick = useCallback((action: SettlementAction) => {
    if (action === "EDIT") {
      formHandler.open()
    }
  }, [settlement, formHandler])

  const onSettlementFormSubmit = useCallback((formValues: SettlementFormValues) => {
    const patch: Partial<Settlement> = {
      status: formValues.status.code,
      name: formValues.name,
    }

    updateSettlement({
      ...settlement.asTypeValue(),
      ...patch,
    }, () => {
      formHandler.close()
    })
  }, [updateSettlement, settlement, formHandler])

  const items = tabs.map((tab) => (
    <HasAccess accessOnEmpty={true} permissions={tab.permissions} key={tab.key}>
      <Tabs.Tab value={tab.key}>
        {tab.value}
      </Tabs.Tab>
    </HasAccess>
  ));

  return (
    <Card shadow="sm" padding="lg" mb="lg" radius="md" withBorder>
      <Container fluid={true} className={classes.mainSection}>
        <Group position="apart">
          <Title order={4}>Settlement: <FallbackTitle {...props} /></Title>
          <Menu
            width={260}
            position="bottom-end"
            transitionProps={{transition: "pop-top-right"}}
            withinPortal
          >
            <Menu.Target>
              <UnstyledButton>
                <Group spacing={7}>
                  <Text weight={500} size="sm" sx={{lineHeight: 1}} mr={3}>
                    {t("common.action.plural", "Actions")}
                  </Text>
                  <IconChevronDown size={rem(12)} stroke={1.5}/>
                </Group>
              </UnstyledButton>
            </Menu.Target>
            <Menu.Dropdown>
              <SettlementActionMenuDropdown
                onActionClick={onSettlementActionClick}
                settlement={settlement}/>
            </Menu.Dropdown>
          </Menu>
        </Group>
      </Container>

      <Card.Section>
        <Container fluid={true}>
          <Tabs
            defaultValue={state?.tabValue ?? "details"}
            onTabChange={(value) => navigate(pathname + search, {replace: true, state: {...state, tabValue: value}})}
          >
            <Tabs.List>{items}</Tabs.List>
            <Space h="md"/>
            <Tabs.Panel value="details">
              <SettlementDetails settlement={settlement}/>
            </Tabs.Panel>
            <Tabs.Panel value="insights">
              <SettlementInsights settlement={settlement}/>
            </Tabs.Panel>
            <Tabs.Panel value="history">
              <ObjectChangeLogView entity={settlement}/>
            </Tabs.Panel>
          </Tabs>
        </Container>
      </Card.Section>

      <SettlementFormModal
        opened={formOpen}
        onClose={formHandler.close}
        onSubmit={onSettlementFormSubmit}
        submitting={isUpdating}
        options={{
          regionId: settlement.regionId ?? regionId,
        }}
        defaultValues={{
          name: settlement.name,
          status: settlement.status,
        }}/>
    </Card>
  );
};
